import { Address, fromNano, toNano } from '@ton/core';
import { assertConfig, buildPauseLaunchesBody, buildSetConfigBody, buildSetProtocolBody, type CurveConfig } from '@gong/curve-math';
import { useCallback, useEffect, useState } from 'react';
import { FACTORY_ADDRESS, IS_TESTNET } from '../config';
import { loadFactory, loadFactoryParams, loadFeeVault, loadLaunch, type FactorySnapshot, type FeeVaultSnapshot } from '../chain/ton';
import { api } from '../lib/api';
import { cellToBase64, type TonConnectTx } from '../quotes/assemble';

type FeatureKey = 'launchChannels' | 'inline' | 'discovery';
type AdminData = { features: Record<FeatureKey, boolean>; botBuyMode: 'miniapp' | 'tonkeeper';
  discovery: { launchChat: string | null; graduationChat: string | null } };
type ContractItem = { curve_address: string; minter_address: string | null; locker_address: string | null;
  phase: string; name: string | null; ticker: string | null };
type Field = { key: string; label: string; unit: 'TON' | 'tokens' | 'bps' | 's' };
const fields: Field[] = [
  { key: 'totalSupply', label: 'Общая эмиссия', unit: 'tokens' },
  { key: 'curveSupply', label: 'На кривой', unit: 'tokens' },
  { key: 'virtualTon', label: 'Виртуальный TON', unit: 'TON' },
  { key: 'virtualJettons', label: 'Виртуальные токены', unit: 'tokens' },
  { key: 'graduationThreshold', label: 'Порог градуации', unit: 'TON' },
  { key: 'tradeFeeBps', label: 'Комиссия торговли', unit: 'bps' },
  { key: 'graduationFeeBps', label: 'Комиссия градуации', unit: 'bps' },
  { key: 'routerFeeBps', label: 'Комиссия роутера', unit: 'bps' },
  { key: 'antiSnipeMaxBps', label: 'Антиснайп максимум', unit: 'bps' },
  { key: 'antiSnipeWindowSeconds', label: 'Антиснайп окно', unit: 's' },
  { key: 'devBuyMaxBps', label: 'Лимит dev-buy', unit: 'bps' },
  ...(['feeSplit', 'routerSplit', 'harvestSplit'] as const).flatMap((group) =>
    (['channelBps', 'protocolBps', 'referrerBps'] as const).map((part) => ({
      key: `${group}.${part}`, label: `${{ feeSplit: 'Торговля', routerSplit: 'Роутер', harvestSplit: 'Harvest' }[group]} · ${
        { channelBps: 'Канал', protocolBps: 'Протокол', referrerBps: 'Реферер' }[part]}`, unit: 'bps' as const,
    }))),
];

function friendly(address: Address): string {
  return address.toString({ testOnly: IS_TESTNET, bounceable: true, urlSafe: true });
}

function draftFrom(config: CurveConfig): Record<string, string> {
  return Object.fromEntries(fields.map(({ key, unit }) => {
    const [first, second] = key.split('.');
    const raw = second
      ? config[first as 'feeSplit' | 'routerSplit' | 'harvestSplit'][second as 'channelBps']
      : config[first as keyof CurveConfig];
    return [key, unit === 'TON' || unit === 'tokens' ? fromNano(raw as bigint) : String(raw)];
  }));
}

function configFrom(draft: Record<string, string>, original: CurveConfig): CurveConfig {
  const config: CurveConfig = { ...original, feeSplit: { ...original.feeSplit },
    routerSplit: { ...original.routerSplit }, harvestSplit: { ...original.harvestSplit } };
  for (const { key, unit } of fields) {
    const value = draft[key]?.trim() ?? '';
    if (!value || !/^(?:0|[1-9]\d*)(?:\.\d{1,9})?$/.test(value) || ((unit === 'bps' || unit === 's') && value.includes('.'))) {
      throw new Error(`Проверьте поле «${key}».`);
    }
    const parsed = unit === 'TON' || unit === 'tokens' ? toNano(value) : BigInt(value);
    if (unit === 'bps' && parsed > 10_000n) throw new Error(`${key}: максимум 10 000 bps.`);
    const [first, second] = key.split('.');
    if (second) config[first as 'feeSplit' | 'routerSplit' | 'harvestSplit'][second as 'channelBps'] = parsed;
    else if (first === 'antiSnipeWindowSeconds') config.antiSnipeWindowSeconds = Number(parsed);
    else (config as unknown as Record<string, bigint>)[first!] = parsed;
  }
  assertConfig(config);
  return config;
}

function TokenContract({ item }: { item: ContractItem }) {
  const [params, setParams] = useState<CurveConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  return <details className="admin-contract" onToggle={(e) => {
    if (!e.currentTarget.open || params || error) return;
    void loadLaunch(Address.parse(item.curve_address)).then((launch) => setParams(launch.config))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Не удалось прочитать кривую.'));
  }}>
    <summary><strong>{item.name ?? item.ticker ?? 'Токен'} · {item.phase}</strong></summary>
    <div className="admin-row"><span>Кривая</span><code>{item.curve_address}</code></div>
    <div className="admin-row"><span>Минтер</span><code>{item.minter_address ?? '—'}</code></div>
    <div className="admin-row"><span>LP-локер</span><code>{item.locker_address ?? '—'}</code></div>
    {params ? <><p className="hint">Параметры этой кривой зафиксированы при запуске.</p>{fields.map(({ key, label, unit }) =>
      <div className="admin-row" key={key}><span>{label}</span><strong>{draftFrom(params)[key]} {unit}</strong></div>)}</>
      : <p className="hint">{error ?? 'Загружаем параметры кривой…'}</p>}
  </details>;
}

export function AdminScreen({ session, walletAddress, sendTx }: {
  session: string; walletAddress: string;
  sendTx: (tx: TonConnectTx) => Promise<boolean>;
}) {
  const [admin, setAdmin] = useState<AdminData | null>(null);
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [factory, setFactory] = useState<FactorySnapshot | null>(null);
  const [vault, setVault] = useState<FeeVaultSnapshot | null>(null);
  const [config, setConfig] = useState<CurveConfig | null>(null);
  const [factoryFieldCount, setFactoryFieldCount] = useState<13 | 20>(20);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [treasury, setTreasury] = useState('');
  const [feeVault, setFeeVault] = useState('');
  const [protocol, setProtocol] = useState('');
  const [launchFee, setLaunchFee] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    const [data, inventory] = await Promise.all([api<AdminData>('/api/admin', session),
      api<{ items: ContractItem[] }>('/api/admin/contracts', session)]);
    const address = Address.parse(FACTORY_ADDRESS);
    const snapshot = await loadFactory(address);
    const params = await loadFactoryParams(address);
    const fee = snapshot.feeVault ? await loadFeeVault(snapshot.feeVault) : null;
    setAdmin(data); setContracts(inventory.items); setFactory(snapshot); setConfig(params.config);
    setFactoryFieldCount(params.fieldCount); setVault(fee);
    setDraft(draftFrom(params.config)); setTreasury(snapshot.treasury?.toRawString() ?? '');
    setFeeVault(snapshot.feeVault?.toRawString() ?? ''); setLaunchFee(fromNano(snapshot.launchFee));
    setProtocol(fee?.protocol?.toRawString() ?? '');
  }, [session]);
  useEffect(() => { void refresh().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось загрузить настройки.')); }, [refresh]);

  async function saveSetting(body: unknown) {
    setBusy(true); setError(null);
    try { await api('/api/admin', session, body, 'PUT'); await refresh(); setNotice('Настройка сохранена.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Не удалось сохранить настройку.'); }
    finally { setBusy(false); }
  }
  async function submit(address: Address, payload: ReturnType<typeof buildSetProtocolBody>) {
    const sent = await sendTx({ validUntil: Math.floor(Date.now() / 1000) + 180,
      messages: [{ address: friendly(address), amount: toNano('0.08').toString(), payload: cellToBase64(payload) }] });
    if (sent) setNotice('Транзакция отправлена. Обновите данные после подтверждения в сети.');
  }
  const walletRaw = (() => { try { return Address.parse(walletAddress).toRawString(); } catch { return ''; } })();
  const canSign = Boolean(factory && walletRaw === factory.admin.toRawString());

  return <main className="scroll admin-screen">
    <div className="eyebrow">GONG · УПРАВЛЕНИЕ TESTNET</div>
    <h2>Админ-панель</h2>
    <p className="hint">Изменения фабрики относятся только к будущим запускам. Существующие кривые, минтеры и LP-локеры сохраняют свои параметры.</p>
    {error && <div className="banner warn" role="alert">{error}</div>}
    {notice && <div className="banner ok">{notice}</div>}
    {!admin || !factory || !config ? <p className="hint">Загружаем настройки контрактов…</p> : <>
      <section className="admin-card"><h3>Контракты</h3>
        <div className="admin-row"><span>Фабрика</span><code>{FACTORY_ADDRESS}</code></div>
        <div className="admin-row"><span>Админ кошелёк</span><code>{factory.admin.toRawString()}</code></div>
        <div className="admin-row"><span>FeeVault</span><code>{factory.feeVault?.toRawString() ?? '—'}</code></div>
        <div className="admin-row"><span>Запусков</span><strong>{factory.launchCount}</strong></div>
        {vault && <><div className="admin-row"><span>Накоплено протоколу</span><strong>{vault.protocolAccrued === null ? 'getter недоступен' : `${fromNano(vault.protocolAccrued)} TON`}</strong></div>
          <div className="admin-row"><span>Всего к выплате</span><strong>{vault.totalAccrued === null ? 'getter недоступен' : `${fromNano(vault.totalAccrued)} TON`}</strong></div></>}
      </section>
      <section className="admin-card"><h3>Созданные токены · {contracts.length}</h3>
        <p className="hint">Кривые, минтеры и LP-локеры доступны только для просмотра: после запуска их параметры не меняются администратором.</p>
        {contracts.map((item) => <TokenContract item={item} key={item.curve_address} />)}
      </section>
      <section className="admin-card"><h3>Покупка через бот</h3>
        <div className="admin-options">
          <label><input type="radio" name="buy-mode" checked={admin.botBuyMode === 'miniapp'} disabled={busy}
            onChange={() => void saveSetting({ botBuyMode: 'miniapp' })} /> Через миниапп</label>
          <label><input type="radio" name="buy-mode" checked={admin.botBuyMode === 'tonkeeper'} disabled={busy}
            onChange={() => void saveSetting({ botBuyMode: 'tonkeeper' })} /> Сразу в кошельке · Tonkeeper</label>
        </div></section>
      <section className="admin-card"><h3>Feature toggles</h3>
        {([['launchChannels', 'Привязка каналов при запуске'], ['inline', 'Inline-карточки бота'], ['discovery', 'Дискавери-канал']] as const).map(([key, label]) =>
          <label className="admin-switch" key={key}><span>{label}</span><input type="checkbox" checked={admin.features[key]} disabled={busy}
            onChange={(e) => void saveSetting({ feature: key, enabled: e.target.checked })} /></label>)}
        <p className="hint">Дискавери: {admin.discovery.launchChat ?? 'не настроен'} · градуации: {admin.discovery.graduationChat ?? 'не настроены'}</p>
      </section>
      <section className="admin-card"><h3>Фабрика · будущие запуски</h3>
        <p className="hint">{canSign ? 'Подтвердите изменение кошельком администратора.' : 'Для транзакций подключите кошелёк администратора фабрики.'}</p>
        {factoryFieldCount === 13 && <p className="hint">Эта фабрика использует прежний формат из 13 параметров. Настройки роутера и harvest в ней отсутствуют.</p>}
        <label>Плата за запуск · TON<input value={launchFee} onChange={(e) => setLaunchFee(e.target.value)} /></label>
        <label>Казна<input value={treasury} onChange={(e) => setTreasury(e.target.value)} /></label>
        <label>FeeVault для новых запусков<input value={feeVault} onChange={(e) => setFeeVault(e.target.value)} /></label>
        <div className="admin-fields">{fields.filter((field) => factoryFieldCount === 20 || field.key !== 'routerFeeBps' && !field.key.startsWith('routerSplit.') && !field.key.startsWith('harvestSplit.')).map((field) => <label key={field.key}>{field.label} · {field.unit}
          <input inputMode="decimal" value={draft[field.key] ?? ''} onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))} /></label>)}</div>
        <p className="hint">Каждый сплит торговых, роутерных и harvest-комиссий должен составлять 10 000 bps; у harvest доля реферера равна 0.</p>
        <button className="btn" disabled={!canSign || busy} onClick={() => { try { setError(null); void submit(Address.parse(FACTORY_ADDRESS), buildSetConfigBody({
          config: configFrom(draft, config), treasury: Address.parse(treasury), feeVault: Address.parse(feeVault), launchFee: toNano(launchFee),
          fieldCount: factoryFieldCount,
        })).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Ошибка транзакции.')); }
        catch (e) { setError(e instanceof Error ? e.message : 'Некорректные параметры.'); } }}>Отправить конфиг</button>
        <button className="btn secondary" disabled={!canSign || busy} onClick={() => void submit(Address.parse(FACTORY_ADDRESS), buildPauseLaunchesBody(!factory.paused)).catch((e: unknown) => setError(String(e)))}>
          {factory.paused ? 'Возобновить запуски' : 'Приостановить запуски'}</button>
      </section>
      <section className="admin-card"><h3>FeeVault · получатель протокола</h3>
        <p className="hint">Смена адреса перенаправит и уже накопленную долю протокола.</p>
        <label>Кошелёк протокола<input value={protocol} onChange={(e) => setProtocol(e.target.value)} /></label>
        <button className="btn" disabled={!canSign || !factory.feeVault || busy} onClick={() => { try { setError(null); void submit(factory.feeVault!, buildSetProtocolBody(Address.parse(protocol))).catch((e: unknown) => setError(String(e))); }
          catch (e) { setError(e instanceof Error ? e.message : 'Некорректный адрес.'); } }}>Сменить получателя</button>
      </section>
      <button className="btn secondary" onClick={() => void refresh().catch((e: unknown) => setError(String(e)))}>Обновить из сети</button>
    </>}
  </main>;
}
